import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import type { IScheduleSummary } from '@/store/types';

import { media } from '@/styles/theme';
import { Card, LoaderWrapper } from '@/components/common';
import { Schedules as SchedulesSvg } from '@/assets/icons';
import { classList } from '@/helpers';
import { MAX_CONTRACT_DETAILS_WIDGET_HEIGHT } from '@/config/variables';
import { SchedulesSkeleton, SchedulesSkeletonFullWidth } from './Schedules.skeleton';

interface ISchedules {
  agentContractSchedules: IScheduleSummary[] | null;
  fullWidth?: boolean;
  isLoading?: boolean;
}

const Schedules = ({ agentContractSchedules, fullWidth, isLoading = false }: ISchedules): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <StyledLoaderWrapper
      fullWidth={fullWidth}
      isLoading={isLoading}
      loader={fullWidth ? <SchedulesSkeletonFullWidth /> : <SchedulesSkeleton />}
    >
      <Container className={classList(fullWidth && 'full-width')}>
        <Card
          name={t`gigCompliance.schedules.title`}
          icon={SchedulesSvg}
          maxBodyHeight={!fullWidth ? MAX_CONTRACT_DETAILS_WIDGET_HEIGHT : undefined}
          bodyHeight={!fullWidth ? MAX_CONTRACT_DETAILS_WIDGET_HEIGHT : undefined}
        >
          <ScheduleContainer>
            {agentContractSchedules?.map((schedule, index) => (
              <ScheduleItem key={`${schedule.scheduleId}${index}`}>
                {index + 1}. {schedule.scheduleName}
              </ScheduleItem>
            ))}
            {!agentContractSchedules && <div>{t`gigCompliance.schedules.noSchedules`}</div>}
          </ScheduleContainer>
        </Card>
      </Container>
    </StyledLoaderWrapper>
  );
};

export { Schedules };

const Container = styled.div`
  width: 100%;

  ${media.tablet} {
    margin: 16px 9px;
    width: 25%;

    &.full-width {
      margin: 0;
      width: 100%;
    }
  }
`;

const StyledLoaderWrapper = styled(LoaderWrapper)<{ fullWidth: boolean | undefined }>`
  ${media.tablet} {
    width: ${({ fullWidth }) => (fullWidth ? '100%' : '25%')};
  }
`;

const ScheduleContainer = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #5a5a5a;
  padding: 15px 14px;
`;

const ScheduleItem = styled.div`
  padding-bottom: 14px;

  &:last-child {
    padding: 0;
  }
`;
